import { useLayoutEffect, useRef, useState } from "react";

/** One point's readout, pre-formatted by the caller — it owns the payload's axis formats. */
export interface SparklineReadout {
  label: string;
  value: string;
  /** The comparison series at the same point. Absent where it has no value there yet. */
  comparisonValue?: string;
}

interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  strokeWidth?: number;
  fillOpacity?: number;
  /**
   * Per-point readouts, aligned by index with `data`. Supplied turns the sparkline into a
   * hoverable chart; omitted leaves it the static graphic it was.
   *
   * Separate from `data` because `data` is the plotted geometry, which for an equity curve is
   * log-scaled — hovering "3.04" over a point that reads 1100x would be worse than no readout.
   */
  readouts?: SparklineReadout[];
  /**
   * A second series on the same scale, aligned by index with `data`.
   *
   * Null is a gap, not a zero: the alt series starts when its vehicle did, and both series divide
   * by the same starting balance, so they share one min and max. Rebasing the second to its own
   * range would draw a comparison that is not one.
   */
  comparison?: (number | null)[];
  /** Stroke for `comparison`. The base series stays on currentColor, as the parent drives it. */
  comparisonColor?: string;
}

// Tiny inline-SVG sparkline. Uses currentColor so the parent can drive the
// stroke/fill via `style={{ color: ... }}` or a CoreUI text-* class.
export function Sparkline({
  data,
  width = 260,
  height = 40,
  strokeWidth = 1.5,
  fillOpacity = 0.12,
  readouts,
  comparison,
  comparisonColor,
}: SparklineProps) {
  const [hovered, setHovered] = useState<number | null>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const readoutRef = useRef<HTMLDivElement>(null);
  const [readoutLeft, setReadoutLeft] = useState(0);

  // Track the marker, as every other chart's tooltip does, but keep the box inside the card: a
  // 260px sparkline is narrow enough that a readout centred on either end would hang off it.
  // Measured rather than guessed because the width changes with the label - "Dec 2025 1240x" is
  // wider than "Jan 2016 1.0x", and a stale half-width clamps to the wrong place.
  useLayoutEffect(() => {
    const host = hostRef.current;
    const readout = readoutRef.current;

    if (!host || !readout || hovered === null || data.length < 2) return;

    const hostWidth = host.clientWidth;
    const half = readout.offsetWidth / 2;
    const markerX = (hovered / (data.length - 1)) * hostWidth;

    setReadoutLeft(Math.min(hostWidth - half, Math.max(half, markerX)));
  }, [hovered, data.length]);

  if (data.length < 2) return null;

  // One scale across both series - see `comparison`.
  const comparisonValues = (comparison ?? []).filter((v): v is number => v !== null);
  const min = Math.min(...data, ...comparisonValues);
  const max = Math.max(...data, ...comparisonValues);
  const range = max - min || 1;

  const xAt = (i: number) => (i / (data.length - 1)) * width;
  const yAt = (v: number) => height - ((v - min) / range) * (height - 4) - 2;

  const points = data.map((v, i) => `${xAt(i)},${yAt(v).toFixed(2)}`).join(" ");
  const areaPoints = `0,${height} ${points} ${width},${height}`;

  // A polyline cannot carry a gap, so a series with leading nulls becomes one polyline per run of
  // real values rather than a line drawn back to zero.
  const comparisonRuns: string[] = [];
  let run: string[] = [];
  (comparison ?? []).forEach((v, i) => {
    if (v === null) {
      if (run.length > 1) comparisonRuns.push(run.join(" "));
      run = [];
      return;
    }
    run.push(`${xAt(i)},${yAt(v).toFixed(2)}`);
  });
  if (run.length > 1) comparisonRuns.push(run.join(" "));

  const interactive = !!readouts?.length;
  const active = hovered !== null ? readouts?.[hovered] : undefined;

  // The SVG is stretched by preserveAspectRatio="none", so the pointer has to be mapped through
  // the element's rendered width rather than the viewBox's.
  const handleMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width === 0) return;

    const ratio = (e.clientX - rect.left) / rect.width;
    const index = Math.round(ratio * (data.length - 1));

    setHovered(Math.min(data.length - 1, Math.max(0, index)));
  };

  return (
    <div ref={hostRef} style={{ position: "relative" }}>
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        style={{ display: "block", cursor: interactive ? "crosshair" : undefined }}
        onMouseMove={interactive ? handleMove : undefined}
        onMouseLeave={interactive ? () => setHovered(null) : undefined}
        aria-hidden
      >
        <polygon points={areaPoints} fill="currentColor" fillOpacity={fillOpacity} />
        <polyline
          points={points}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Unfilled, matching the full chart: an area drawn over the base curve hides it. */}
        {comparisonRuns.map((segment) => (
          <polyline
            key={segment}
            points={segment}
            fill="none"
            stroke={comparisonColor ?? "currentColor"}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
        {hovered !== null && (
          <>
            <line
              x1={xAt(hovered)}
              y1={0}
              x2={xAt(hovered)}
              y2={height}
              stroke="currentColor"
              strokeWidth={1}
              strokeOpacity={0.4}
              // The viewBox is stretched horizontally, which would smear a plain stroke into a
              // wedge. Cancelling the scale keeps the marker a hairline at any card width.
              vectorEffect="non-scaling-stroke"
            />
            <circle
              cx={xAt(hovered)}
              cy={yAt(data[hovered])}
              r={2.5}
              fill="currentColor"
              vectorEffect="non-scaling-stroke"
            />
          </>
        )}
      </svg>

      {active && (
        <div
          ref={readoutRef}
          className="position-absolute top-0 rounded px-2 py-1 text-nowrap"
          style={{
            left: readoutLeft,
            // Both axes in one transform: centred on the marker, and lifted clear above it.
            transform: "translate(-50%, -100%)",
            backgroundColor: "var(--cui-body-color)",
            color: "var(--cui-body-bg)",
            fontSize: "0.7rem",
            lineHeight: 1.3,
            pointerEvents: "none",
            zIndex: 2,
          }}
        >
          <span style={{ opacity: 0.7 }}>{active.label}</span>{" "}
          <strong className="ms-1">{active.value}</strong>
          {/* Only where the comparison has reached this point. A dash would read as a value. */}
          {active.comparisonValue && (
            <strong className="ms-2" style={{ color: comparisonColor }}>
              {active.comparisonValue}
            </strong>
          )}
        </div>
      )}
    </div>
  );
}
